import React from 'react';

interface ExportButtonProps {
  onClick: () => void;
  loading?: boolean;
  disabled?: boolean;
  title?: string;
  size?: 'small' | 'medium' | 'large';
  variant?: 'primary' | 'secondary';
  showIcon?: boolean;
}

const ExportButton: React.FC<ExportButtonProps> = ({
  onClick,
  loading = false,
  disabled = false,
  title = 'Export to Excel',
  size = 'medium',
  variant = 'primary',
  showIcon = true,
}) => {
  const sizeStyles = {
    small: {
      padding: '0.25rem 0.5rem',
      fontSize: '0.75rem',
      gap: '0.25rem',
    },
    medium: {
      padding: '0.25rem 0.5rem',
      fontSize: '0.85rem',
      gap: '0.25rem',
    },
    large: {
      padding: '0.5rem 1rem',
      fontSize: '0.9rem',
      gap: '0.5rem',
      fontWeight: '500' as const,
    },
  };

  const variantColors = {
    primary: {
      backgroundColor: loading || disabled ? '#6c757d' : '#28a745',
      hoverColor: '#218838',
    },
    secondary: {
      backgroundColor: loading || disabled ? '#6c757d' : '#007bff',
      hoverColor: '#0056b3',
    },
  };

  const currentSize = sizeStyles[size];
  const currentVariant = variantColors[variant];

  return (
    <button
      onClick={onClick}
      disabled={disabled || loading}
      style={{
        ...currentSize,
        backgroundColor: currentVariant.backgroundColor,
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: loading || disabled ? 'not-allowed' : 'pointer',
        display: 'flex',
        alignItems: 'center',
        transition: 'background-color 0.2s ease',
      }}
      onMouseEnter={(e) => {
        if (!loading && !disabled) {
          e.currentTarget.style.backgroundColor = currentVariant.hoverColor;
        }
      }}
      onMouseLeave={(e) => {
        if (!loading && !disabled) {
          e.currentTarget.style.backgroundColor = currentVariant.backgroundColor;
        }
      }}
      title={title}
    >
      {loading ? (
        <>
          <svg
            style={{
              width: size === 'small' ? '10px' : size === 'medium' ? '12px' : '14px',
              height: size === 'small' ? '10px' : size === 'medium' ? '12px' : '14px',
              animation: 'spin 1s linear infinite',
            }}
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              style={{ opacity: 0.25 }}
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              style={{ opacity: 0.75 }}
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          <span>Exporting...</span>
        </>
      ) : (
        <>
          {showIcon && <span>⬇</span>}
          <span>Export</span>
        </>
      )}
      <style>{`
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </button>
  );
};

export default ExportButton;
